'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Church, Clock, Contact, Gamepad2, Layers, Loader2, Music2, Search, Shield,
  Sparkles, Users, X,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Input } from '@/components/ui/input';
import { getCarnet, searchUsersByNickname, type CarnetData, type CarnetEntidad } from '@/lib/actions';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

/**
 * Carnet de hermano: la ficha de un ciudadano de Jartiland juntando lo que la
 * plataforma ya sabe de él y hoy vive repartido (hermandades, bandas,
 * papeletas de sitio, cromos y horas jugadas). Los datos los arma la Cloud
 * Function `getCarnet`, porque casi nada de esto se puede leer desde el cliente.
 *
 * `/jartinet/carnet` es el tuyo; `/jartinet/carnet/:uid`, el de otra persona.
 */

const fechaLarga = (iso: string | null) => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : format(d, "d 'de' MMMM 'de' yyyy", { locale: es });
};

function StatTile({ icon, valor, label, hint, color }: {
  icon: React.ReactNode;
  valor: string | number;
  label: string;
  hint?: string;
  color: string;
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/8 bg-card/40 p-4">
      <div className="pointer-events-none absolute -right-6 -top-6 h-20 w-20 rounded-full blur-2xl" style={{ background: `${color}33` }} />
      <div className="relative flex items-center gap-3">
        <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl border" style={{ borderColor: `${color}55`, background: `${color}1a`, color }}>
          {icon}
        </div>
        <div className="min-w-0">
          <p className="text-xl font-black leading-none">{valor}</p>
          <p className="mt-1 truncate text-[0.7rem] uppercase tracking-wider text-muted-foreground">{label}</p>
        </div>
      </div>
      {hint && <p className="relative mt-2 text-[0.65rem] text-muted-foreground">{hint}</p>}
    </div>
  );
}

/** Tarjeta de una entidad a la que pertenece, con su color. */
function EntidadCard({ entidad, to, badge }: { entidad: CarnetEntidad; to: string; badge?: string }) {
  const color = entidad.colorPrimario || '#64748b';
  return (
    <Link
      to={to}
      className="group relative flex items-center gap-3 overflow-hidden rounded-2xl border border-white/8 bg-card/40 p-3 transition-colors hover:border-white/25"
    >
      <span className="absolute inset-y-0 left-0 w-[3px]" style={{ background: color }} />
      {entidad.escudoUrl ? (
        <img src={entidad.escudoUrl} alt="" className="ml-1 h-10 w-10 flex-shrink-0 object-contain" loading="lazy" />
      ) : (
        <span className="ml-1 grid h-10 w-10 flex-shrink-0 place-items-center rounded-full bg-white/5">
          <Church className="h-4 w-4 text-muted-foreground" />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{entidad.nombre}</p>
        <p className="truncate text-[0.7rem] text-muted-foreground">
          {[entidad.tipo, entidad.dia].filter(Boolean).join(' · ') || entidad.nombreCompleto}
        </p>
      </div>
      {badge && (
        <span className="flex-shrink-0 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[0.62rem] font-bold text-primary">
          {badge}
        </span>
      )}
    </Link>
  );
}

function Seccion({ icon, titulo, cuenta, children }: {
  icon: React.ReactNode;
  titulo: string;
  cuenta: number;
  children: React.ReactNode;
}) {
  if (!cuenta) return null;
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-primary">{icon}</span>
        <h2 className="font-bold">{titulo}</h2>
        <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[0.65rem] font-semibold text-muted-foreground">
          {cuenta}
        </span>
      </div>
      {children}
    </section>
  );
}

/** Buscador de ciudadanos para saltar de un carnet a otro. */
function BuscadorCiudadanos() {
  const [q, setQ] = useState('');
  const [resultados, setResultados] = useState<{ id: string; nickname: string; profileImageUrl: string | null }[]>([]);
  const [buscando, setBuscando] = useState(false);
  const temporizador = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (temporizador.current) clearTimeout(temporizador.current);
    const texto = q.trim();
    if (texto.length < 2) { setResultados([]); return; }
    // El buscador del backend escanea la colección entera, así que se espera a
    // que se deje de teclear antes de preguntar.
    setBuscando(true);
    temporizador.current = setTimeout(async () => {
      const res = await searchUsersByNickname(texto);
      setResultados(res.users || []);
      setBuscando(false);
    }, 400);
    return () => { if (temporizador.current) clearTimeout(temporizador.current); };
  }, [q]);

  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Buscar el carnet de otro ciudadano…"
        className="pl-9 pr-9"
      />
      {q && (
        <button
          type="button"
          onClick={() => setQ('')}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      )}

      {q.trim().length >= 2 && (
        <div className="absolute z-20 mt-2 w-full overflow-hidden rounded-2xl border border-white/10 bg-card/95 shadow-xl backdrop-blur-xl">
          {buscando && <p className="px-4 py-3 text-sm text-muted-foreground">Buscando…</p>}
          {!buscando && resultados.length === 0 && (
            <p className="px-4 py-3 text-sm text-muted-foreground">Ningún ciudadano con ese nombre.</p>
          )}
          {resultados.map((u) => (
            <Link
              key={u.id}
              to={`/jartinet/carnet/${u.id}`}
              onClick={() => setQ('')}
              className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-white/5"
            >
              {u.profileImageUrl ? (
                <img src={u.profileImageUrl} alt="" className="h-8 w-8 rounded-full object-cover" />
              ) : (
                <span className="grid h-8 w-8 place-items-center rounded-full bg-primary/15 text-xs font-bold text-primary">
                  {u.nickname?.[0]?.toUpperCase() || '?'}
                </span>
              )}
              <span className="truncate text-sm font-medium">{u.nickname}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CarnetPage() {
  const { uid } = useParams<{ uid?: string }>();
  const { loading: authLoading } = useAuth();
  const [carnet, setCarnet] = useState<CarnetData | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    const res = await getCarnet(uid);
    if (res.carnet) setCarnet(res.carnet);
    else setError(res.error || 'No se pudo cargar el carnet.');
    setCargando(false);
  }, [uid]);

  useEffect(() => { if (!authLoading) cargar(); }, [authLoading, cargar]);

  if (authLoading || cargando) {
    return (
      <div className="flex h-[calc(100vh-12rem)] items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !carnet) {
    return (
      <div className="space-y-6">
        <BuscadorCiudadanos />
        <div className="rounded-2xl border-2 border-dashed border-white/10 py-16 text-center">
          <Contact className="mx-auto h-12 w-12 text-muted-foreground/40" />
          <h2 className="mt-4 text-lg font-semibold">No se pudo cargar el carnet</h2>
          <p className="mt-1 text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  const { perfil, hermandades, bandas, organizaciones, procesiones, cromos, juego } = carnet;
  const desde = fechaLarga(perfil.desde);
  const ultimaVez = fechaLarga(juego.ultimaVez);
  const porcentajeColeccion = cromos.total ? Math.round((cromos.distintos / cromos.total) * 100) : 0;
  const avatar = perfil.profileImageUrl
    || (perfil.mcUuid || perfil.minecraftId ? `https://mc-heads.net/avatar/${perfil.mcUuid || perfil.minecraftId}/128` : '');

  return (
    <div className="space-y-8 pb-10">
      <BuscadorCiudadanos />

      {/* ── Carnet ── */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="relative overflow-hidden rounded-3xl border border-primary/20 bg-gradient-to-br from-primary/15 via-card/60 to-transparent p-6"
      >
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />
        {/* Filete dorado del borde superior, como el de las tarjetas de entidad. */}
        <span className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-primary via-primary/40 to-transparent" />

        <div className="relative flex flex-wrap items-center gap-5">
          <div className="relative">
            {avatar ? (
              <img
                src={avatar}
                alt={perfil.nickname}
                className="h-24 w-24 flex-shrink-0 rounded-2xl border-2 border-primary/40 object-cover"
              />
            ) : (
              <span className="grid h-24 w-24 flex-shrink-0 place-items-center rounded-2xl border-2 border-primary/40 bg-primary/10 text-3xl font-black text-primary">
                {perfil.nickname[0]?.toUpperCase()}
              </span>
            )}
            {perfil.role === 'admin' && (
              <span className="absolute -bottom-2 -right-2 rounded-full border border-amber-400/40 bg-amber-500/20 px-2 py-0.5 text-[0.6rem] font-black uppercase tracking-wider text-amber-300 backdrop-blur-sm">
                Admin
              </span>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-[0.65rem] font-bold uppercase tracking-[0.3em] text-primary/80">Carnet de hermano</p>
            <h1 className="mt-1 font-headline text-3xl font-black md:text-4xl">{perfil.nickname}</h1>
            {perfil.bio && <p className="mt-1.5 max-w-xl text-sm text-muted-foreground">{perfil.bio}</p>}

            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              {desde && (
                <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1">
                  Ciudadano desde el <strong className="text-foreground">{desde}</strong>
                </span>
              )}
              {perfil.minecraftId && (
                <span className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1">
                  <Gamepad2 className="h-3 w-3 text-primary" />
                  {perfil.minecraftId}
                </span>
              )}
              {perfil.email && (
                <span className="rounded-full border border-sky-500/25 bg-sky-500/10 px-2.5 py-1 text-sky-300">
                  {perfil.email}
                </span>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* ── Cifras ── */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          icon={<Shield className="h-5 w-5" />}
          valor={hermandades.length}
          label={hermandades.length === 1 ? 'Hermandad' : 'Hermandades'}
          color="#60a5fa"
        />
        <StatTile
          icon={<Church className="h-5 w-5" />}
          valor={procesiones.length}
          label={procesiones.length === 1 ? 'Procesión' : 'Procesiones'}
          hint="Según sus papeletas de sitio"
          color="#fbbf24"
        />
        <StatTile
          icon={<Layers className="h-5 w-5" />}
          valor={`${cromos.distintos}/${cromos.total || '—'}`}
          label="JartiCards"
          hint={`${porcentajeColeccion}% de la colección · ${cromos.copias} copias`}
          color="#a78bfa"
        />
        <StatTile
          icon={<Clock className="h-5 w-5" />}
          valor={juego.vinculado ? `${juego.horas} h` : '—'}
          label="En el servidor"
          hint={juego.vinculado ? (ultimaVez ? `Última vez: ${ultimaVez}` : undefined) : 'Sin cuenta de Minecraft vinculada'}
          color="#34d399"
        />
      </div>

      <Seccion icon={<Shield className="h-4 w-4" />} titulo="Hermandades" cuenta={hermandades.length}>
        <div className="grid gap-2 sm:grid-cols-2">
          {hermandades.map((h) => (
            <EntidadCard
              key={h.id}
              entidad={h}
              to={`/hermandades/${h.id}`}
              badge={h.esHermanoMayor ? 'Hermano mayor' : undefined}
            />
          ))}
        </div>
      </Seccion>

      <Seccion icon={<Music2 className="h-4 w-4" />} titulo="Bandas que dirige" cuenta={bandas.length}>
        <div className="grid gap-2 sm:grid-cols-2">
          {bandas.map((b) => <EntidadCard key={b.id} entidad={b} to={`/bandas/${b.id}`} badge="Director" />)}
        </div>
      </Seccion>

      <Seccion icon={<Users className="h-4 w-4" />} titulo="Organizaciones que gestiona" cuenta={organizaciones.length}>
        <div className="grid gap-2 sm:grid-cols-2">
          {organizaciones.map((o) => <EntidadCard key={o.id} entidad={o} to="/jartinet/organizaciones" badge="Gestor" />)}
        </div>
      </Seccion>

      {/* ── Historial de procesiones (sale de las papeletas de sitio) ── */}
      <Seccion icon={<Church className="h-4 w-4" />} titulo="Procesiones" cuenta={procesiones.length}>
        <div className="space-y-2">
          {procesiones.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-3 rounded-2xl border border-white/8 bg-card/40 p-3 transition-colors hover:border-white/20"
            >
              {p.hermandadEscudo ? (
                <img src={p.hermandadEscudo} alt="" className="h-9 w-9 flex-shrink-0 object-contain" loading="lazy" />
              ) : (
                <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-full bg-white/5">
                  <Church className="h-4 w-4 text-muted-foreground" />
                </span>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{p.hermandadNombre || 'Hermandad'}</p>
                <p className="truncate text-[0.7rem] text-muted-foreground">
                  {[p.dia, p.hora].filter(Boolean).join(' · ') || 'Sin día'}
                </p>
              </div>
              {p.cargo && (
                <span className="flex-shrink-0 rounded-full border border-primary/25 bg-primary/10 px-2.5 py-1 text-[0.68rem] font-semibold text-primary">
                  {p.cargo}
                </span>
              )}
            </div>
          ))}
        </div>
      </Seccion>

      {/* Un carnet vacío no es un error: es alguien que acaba de llegar. */}
      {hermandades.length === 0 && bandas.length === 0 && organizaciones.length === 0 && procesiones.length === 0 && (
        <div className={cn('rounded-2xl border-2 border-dashed border-white/10 py-14 text-center')}>
          <Sparkles className="mx-auto h-10 w-10 text-primary/40" />
          <h2 className="mt-4 text-lg font-semibold">Un carnet en blanco</h2>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            {carnet.esPropio
              ? 'Todavía no perteneces a ninguna hermandad ni has recibido papeletas de sitio. En cuanto lo hagas, aparecerá aquí.'
              : 'Este ciudadano todavía no pertenece a ninguna entidad ni ha salido en ninguna procesión.'}
          </p>
        </div>
      )}
    </div>
  );
}
